import { useId } from 'react'
import {
  Modal, Stack, Group, Button, Text, Code, Divider, Box,
} from '@mantine/core'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { IconRefresh, IconArrowRight } from '@tabler/icons-react'
import { adminTenantsApi } from '@/api/adminTenants'
import type { TenantRow } from './TenantsTable'

interface ProvisioningErrorModalProps {
  opened: boolean
  onClose: () => void
  tenant: TenantRow | null
}

const RTF = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' })

function formatRelative(iso?: string | null): string {
  if (!iso) return '—'
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return iso
  const diffSec = Math.round((ms - Date.now()) / 1000)
  const abs = Math.abs(diffSec)
  if (abs < 60) return RTF.format(diffSec, 'second')
  if (abs < 3600) return RTF.format(Math.round(diffSec / 60), 'minute')
  if (abs < 86400) return RTF.format(Math.round(diffSec / 3600), 'hour')
  return RTF.format(Math.round(diffSec / 86400), 'day')
}

function formatAbsolute(iso?: string | null): string {
  if (!iso) return '—'
  const ms = Date.parse(iso)
  if (Number.isNaN(ms)) return iso
  return new Date(ms).toLocaleString('pt-BR')
}

export function ProvisioningErrorModal({
  opened, onClose, tenant,
}: ProvisioningErrorModalProps) {
  const qc = useQueryClient()
  const titleId = useId()
  const tenantId = tenant?.id ?? 0

  const retryMutation = useMutation({
    mutationFn: () => adminTenantsApi.retryProvisioning(tenantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-tenants'] })
      qc.invalidateQueries({ queryKey: ['provisioning-status', tenantId] })
      notifications.show({
        title: 'Provisionamento reiniciado',
        message: `Nova tentativa iniciada para ${tenant?.name ?? 'tenant'}`,
        color: 'teal',
      })
      onClose()
    },
    onError: () => {
      notifications.show({
        title: 'Erro',
        message: 'Falha ao reiniciar provisionamento',
        color: 'red',
      })
    },
  })

  const skipMutation = useMutation({
    mutationFn: () => adminTenantsApi.skipProvisioning(tenantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-tenants'] })
      qc.invalidateQueries({ queryKey: ['provisioning-status', tenantId] })
      notifications.show({
        title: 'Tenant marcado como fora da Org',
        message: `Provisionamento do tenant ${tenant?.name ?? ''} foi ignorado`,
        color: 'yellow',
      })
      onClose()
    },
    onError: () => {
      notifications.show({
        title: 'Erro',
        message: 'Falha ao marcar tenant como fora da Org',
        color: 'red',
      })
    },
  })

  const isPending = retryMutation.isPending || skipMutation.isPending
  const errorMessage =
    tenant?.provisioning_last_error || tenant?.last_error || 'Sem detalhes adicionais.'

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton
      size="lg"
      aria-labelledby={titleId}
      title={
        <Text id={titleId} fw={700} size="lg">
          Erro de provisionamento — {tenant?.name ?? '—'}
        </Text>
      }
    >
      <Stack gap="md">
        <Group gap="xl" wrap="wrap">
          <Box>
            <Text size="xs" c="dimmed">Account ID</Text>
            <Code>{tenant?.account_id ?? '—'}</Code>
          </Box>
          <Box>
            <Text size="xs" c="dimmed">Operation ID</Text>
            <Code>{tenant?.operation_id ?? '—'}</Code>
          </Box>
        </Group>

        <Group gap="xl" wrap="wrap">
          <Box>
            <Text size="xs" c="dimmed">Iniciado</Text>
            <Text
              size="sm"
              title={formatAbsolute(tenant?.provisioning_started_at)}
            >
              {formatRelative(tenant?.provisioning_started_at)}
            </Text>
          </Box>
          <Box>
            <Text size="xs" c="dimmed">Falhou em</Text>
            <Text
              size="sm"
              title={formatAbsolute(tenant?.provisioning_completed_at)}
            >
              {formatRelative(tenant?.provisioning_completed_at)}
            </Text>
          </Box>
        </Group>

        <Divider />

        <Box>
          <Text size="xs" c="dimmed" mb={4}>Erro do CloudFormation</Text>
          <Box
            component="pre"
            role="region"
            aria-label="Mensagem de erro do CloudFormation"
            tabIndex={0}
            style={{
              background: 'var(--mantine-color-dark-7, #1a1b1e)',
              color: 'var(--mantine-color-red-3, #ff8787)',
              padding: 12,
              borderRadius: 4,
              maxHeight: 240,
              overflow: 'auto',
              fontSize: 12,
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              margin: 0,
            }}
          >
            {errorMessage}
          </Box>
        </Box>

        <Group justify="flex-end" gap="sm">
          <Button
            variant="default"
            onClick={() => skipMutation.mutate()}
            loading={skipMutation.isPending}
            disabled={isPending || !tenantId}
            leftSection={<IconArrowRight size={16} />}
          >
            Marcar como fora da Org
          </Button>
          <Button
            data-autofocus
            color="blue"
            onClick={() => retryMutation.mutate()}
            loading={retryMutation.isPending}
            disabled={isPending || !tenantId}
            leftSection={<IconRefresh size={16} />}
          >
            Tentar novamente
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
